// StyledSelect component
import React, { useMemo, useState, useCallback } from 'react';
import { ValueSelect, StyledSelectContainer, Label } from './styled';
import { Tag } from 'antd';

interface OptionItem {
  label: string;
  value: string;
}

interface StyledSelectProps {
  label: string;
  valueOptions: OptionItem[] | { label: React.ReactNode; value: string }[];
  onChange: (values: string[] | string) => void;
  // Controlled values (optional)
  selectedValues?: string[];
  // UI controls
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  mode?: 'multiple' | 'tags' | undefined;
  maxTagCount?: number;
  allowClear?: boolean;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  minWidth?: string;
  backgroundColor?: string;
}

const StyledSelect: React.FC<StyledSelectProps> = ({
  label,
  valueOptions,
  onChange,
  selectedValues,
  disabled,
  loading,
  placeholder,
  mode = 'multiple',
  maxTagCount = 1,
  allowClear = true,
  getPopupContainer,
  minWidth,
  backgroundColor,
  ...rest
}) => {
  // Uncontrolled fallback
  const [internalValues, setInternalValues] = useState<string[] | string>([]);

  const valuesValue = useMemo(
    () => (selectedValues !== undefined ? selectedValues : internalValues),
    [selectedValues, internalValues],
  );

  // Memoize labelRender to check against current valueOptions
  const labelRender = useCallback(
    (props: { label?: React.ReactNode; value?: string | number }) => {
      // Check if the value still exists in current valueOptions
      const optionExists = valueOptions.some((opt) => String(opt.value) === String(props.value));

      if (optionExists && props.label) {
        return props.label;
      }

      // If option doesn't exist or label is missing, show deleted tag
      return <Tag color="red">Event Deleted</Tag>;
    },
    [valueOptions],
  );

  return (
    <StyledSelectContainer>
      <Label $backgroundColor={backgroundColor}>{label}</Label>
      <ValueSelect
        {...rest}
        labelRender={labelRender}
        options={valueOptions}
        value={valuesValue}
        onChange={(vals) => {
          const newValues = vals;
          if (selectedValues === undefined) {
            setInternalValues(newValues);
          }
          onChange(newValues);
        }}
        mode={mode}
        maxTagCount={maxTagCount}
        disabled={disabled}
        loading={loading}
        allowClear={allowClear}
        placeholder={placeholder}
        getPopupContainer={getPopupContainer}
        $minWidth={minWidth}
        $showRightBorder={true}
        showSearch={true}
        filterOption={(input, option) => {
          const label = option?.label;
          const name = option?.name;
          if (typeof label === 'string') {
            return label.toLowerCase().includes(input.toLowerCase());
          }
          if (name && name.toLowerCase().includes(input.toLowerCase())) {
            return true;
          }
          return false;
        }}
      />
    </StyledSelectContainer>
  );
};

export default StyledSelect;
